/**
 * PWA Install Nudge - Suggests installing the PWA when a delayed roll request is detected.
 * Only shown on mobile, when not already installed as PWA, and not previously dismissed.
 */

import { Link } from 'react-router-dom';
import { Alert } from '@/components/ui/alert';

const STORAGE_KEY = 'pwa-install-nudge-dismissed';
const DELAY_THRESHOLD_MS = 10_000;

interface PwaInstallNudgeProps {
  /** Timestamp (ms) when the roll request was originally created on the backend */
  createdAt?: number;
}

/**
 * Displays an info alert nudging the player to install the PWA to their home screen.
 * Guards: only renders when the roll request was delayed (>10s), app is not installed
 * as a PWA, device is mobile, and the nudge hasn't been permanently dismissed.
 */
export function PwaInstallNudge({ createdAt }: PwaInstallNudgeProps) {
  // No createdAt = live delivery, no delay
  if (createdAt === undefined) return null;

  // Delay too short — normal delivery
  const delayMs = Date.now() - createdAt;
  if (delayMs < DELAY_THRESHOLD_MS) return null;

  // Already running as installed PWA
  if (window.matchMedia('(display-mode: standalone)').matches) return null;

  // Not a mobile device
  if (window.innerWidth > 768) return null;

  return (
    <Alert
      variant="info"
      title="Install Dice Frog for notifications"
      dismissible
      storageKey={STORAGE_KEY}
    >
      <p>
        Your roll request was delayed because your phone was asleep.
        Install Dice Frog to your home screen to receive notifications
        and never miss a roll.
      </p>
      <Link
        to="/about"
        className="text-blue-600 dark:text-blue-400 underline text-xs mt-1 inline-block"
      >
        How to install
      </Link>
    </Alert>
  );
}
